import { FormikProps } from 'formik'
import { RefObject, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { RegisterFormValues } from '../../components/forms/RegisterForm'

const emailRegex = /^(?!.*\.\.)[a-zA-Z0-9](?:[a-zA-Z0-9._-]{0,62}[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/

export const useRegisterForm = ({
  hasAttendant,
  formRef,
  onValidationError,
}: {
  hasAttendant: boolean
  formRef: RefObject<FormikProps<RegisterFormValues>>
  onValidationError?: (errors: any) => void
}) => {
  const { t } = useTranslation()

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string().required(t('register.form.validation.firstName')),
        lastName: Yup.string().required(t('register.form.validation.lastName')),
        email: Yup.string()
          .required(t('register.form.validation.email'))
          .matches(emailRegex, t('register.form.validation.email')),
        terms: Yup.bool().oneOf([true], t('register.form.validation.terms')),
        opt: Yup.bool().oneOf([true, false]),
        ...(hasAttendant && {
          attendant: Yup.object().shape({
            firstName: Yup.string().required(t('register.form.validation.attendant.firstName')),
            lastName: Yup.string().required(t('register.form.validation.attendant.lastName')),
            email: Yup.string()
              .required(t('register.form.validation.attendant.email'))
              .matches(emailRegex, t('register.form.validation.attendant.email.invalid'))
              .test(
                'attendant-email-not-same',
                t('register.form.validation.attendant.notSameAsMain'),
                function (value) {
                  const email = formRef.current?.values.email
                  if (!hasAttendant) return true
                  if (!value || !email) return true
                  return value !== email
                }
              ),
          }),
        }),
      }),
    [formRef, hasAttendant, t]
  )

  const handleValidate = useCallback(
    (values: any) => {
      try {
        validationSchema.validateSync(values, { abortEarly: false })
        return {}
      } catch (err) {
        const errors: { [key: string]: string } = {}
        if (err instanceof Yup.ValidationError && err.inner) {
          err.inner.forEach((e) => {
            if (e.path) errors[e.path] = e.message
          })
        }
        onValidationError?.(Object.values(errors))
        return errors
      }
    },
    [validationSchema, onValidationError]
  )

  return {
    validationSchema,
    handleValidate,
  }
}
